import React, { useContext, useState, useEffect } from 'react'
import { ProjectContext } from '../../../context/ProjectContext'
import { Problems } from './problems'
import { Validation } from './validation'
import { Needs } from './needs'
import { OverviewFeedbacks } from './feedbacks'
import { ResearchQuestions } from './researchQuestions'
import { PeopleToInterview } from './peopleToInterview'
import { Method, Product } from './sidebar/method'
import { Facilitators } from './sidebar/facilitators'
import { useCustomFetch } from '../../../hooks/useCustomFetch'

export function Overview() {
  const {
    validations,
    feedbacks,
    notes,
    problems,
    qas,
    project,
    setProject,
    setNeedsFilters,
    setProblemsFilters,
    setQAsFilters,
  } = useContext(ProjectContext)
  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState(project?.description || '')
  const customFetch = useCustomFetch()

  const handleSave = async () => {
    try {
      await customFetch('/updateProjectNameDescription', 'PUT', {
        id: project.id,
        name: project.name,
        description,
      })
      setProject((prev) => ({ ...prev, description }))
      setIsEditing(false)
    } catch (error) {
      console.error('Error updating project description:', error)
    }
  }

  const hasClusters = (items) =>
    Array.isArray(items) && items.some((item) => Array.isArray(item.clustered_notes) && item.clustered_notes.length > 0)

  // filter by clusters only if there are clusters available
  useEffect(() => {
    const hasNeedsClusters = hasClusters(notes)
    const hasProblemsClusters = hasClusters(problems)
    const hasQAsClusters = hasClusters(qas)

    setNeedsFilters((prev) => ({
      ...prev,
      star: 'all',
      clustersOnly: hasNeedsClusters,
    }))
    setProblemsFilters((prev) => ({
      ...prev,
      star: 'all',
      clustersOnly: hasProblemsClusters,
    }))
    setQAsFilters((prev) => ({
      ...prev,
      star: 'all',
      clustersOnly: hasQAsClusters,
    }))
  }, [notes, problems, qas, setNeedsFilters, setProblemsFilters, setQAsFilters])

  return (
    <div className="w-full flex flex-col gap-[60px]">
      <div className="bg-gradient-to-br from-white via-[#FF4A8E]/20 via-[#8B5CF6]/20 to-[#FFC921]/20 rounded-xl p-6 shadow-sm border border-[#e4e4e4] mt-[20px]">
        <div className="flex flex-col space-y-6">
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="text-[#3f424a] text-4xl font-bold font-['DM_Sans'] leading-[41.40px]">Description</p>
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-[#FF4A8E] text-sm font-medium hover:underline flex items-center gap-1"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                      stroke="#FF4A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18.5 2.50023C18.8978 2.1024 19.4374 1.87891 20 1.87891C20.5626 1.87891 21.1022 2.1024 21.5 2.50023C21.8978 2.89805 22.1213 3.43762 22.1213 4.00023C22.1213 4.56284 21.8978 5.1024 21.5 5.50023L12 15.0002L8 16.0002L9 12.0002L18.5 2.50023Z"
                      stroke="#FF4A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={handleSave} className="text-[#FF4A8E] text-sm font-medium hover:underline">
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setIsEditing(false)
                      setDescription(project?.description || '')
                    }}
                    className="text-[#3f424a] text-sm font-medium hover:underline"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
            {isEditing ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-4 w-full text-[#40424A] text-lg font-normal font-['DM_Sans'] leading-[23px] p-4 bg-white border border-[#e4e4e4] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF4A8E] focus:border-transparent"
                rows={4}
              />
            ) : (
              <p className="mt-4 text-[#40424A] text-lg font-normal font-['DM_Sans'] leading-[23px]">
                {project?.description || 'No description provided'}
              </p>
            )}
          </div>

          <div>
            <div className="flex gap-8">
              <div className="flex-shrink-0 flex gap-4">
                <Product />
                <Method />
              </div>
              <div className="flex-grow">
                <Facilitators overviewDesign={false} />
              </div>
            </div>
          </div>
        </div>
      </div>

      <ResearchQuestions />

      <PeopleToInterview />

      <Problems />

      {notes.length > 0 && <Needs />}

      {feedbacks.length > 0 && <OverviewFeedbacks />}

      {validations.length > 0 && <Validation validations={validations} />}
    </div>
  )
}
